import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Toaster, toast } from 'sonner';
import { 
  User, Mail, Phone, Calendar, ArrowLeft, Heart,
  CheckCircle, Clock, AlertCircle, BookOpen, 
  CreditCard, History, Tag, Send, Users, Edit, Save, X, Trash2, Star, Link2, Copy,
  Plus, Loader2, RefreshCw
} from 'lucide-react';
import Navbar from '../components/Navbar';
import api, { mailingListsAPI, studentsAPI, paymentsAPI, classesAPI } from '../lib/api';


const MidTermToggle = ({ student, onSaved }) => {
  const initialOn = !!student?.is_mid_term;
  const [isMidTerm, setIsMidTerm] = useState(initialOn);
  const [weeksRemaining, setWeeksRemaining] = useState(student?.weeks_remaining || 3);
  const [termLength, setTermLength] = useState(student?.term_length || 6);
  const [saving, setSaving] = useState(false);

  const dirty =
    isMidTerm !== initialOn ||
    (isMidTerm && (Number(weeksRemaining) !== (student?.weeks_remaining || null) ||
                   Number(termLength) !== (student?.term_length || 6)));

  const save = async () => {
    if (isMidTerm) {
      const w = parseInt(weeksRemaining, 10);
      const t = parseInt(termLength, 10);
      if (!w || w < 1) { toast.error('Weeks remaining must be ≥ 1'); return; }
      if (!t || t < w) { toast.error('Term length must be ≥ weeks remaining'); return; }
    }
    setSaving(true);
    try {
      await api.put(`/students/${student.id}/mid-term`, {
        is_mid_term: isMidTerm,
        weeks_remaining: isMidTerm ? parseInt(weeksRemaining, 10) : null,
        term_length: isMidTerm ? parseInt(termLength, 10) : null,
      });
      toast.success(
        isMidTerm
          ? `Pro-rated: ${weeksRemaining}/${termLength} weeks. Bookings will use this when parent registers.`
          : 'Pro-rated flag removed.',
      );
      if (onSaved) await onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
        <input
          type="checkbox"
          checked={isMidTerm}
          onChange={(e) => setIsMidTerm(e.target.checked)}
          className="accent-pink-500"
          data-testid="mid-term-toggle"
        />
        Joining mid-term — apply pro-rated pricing
      </label>

      {isMidTerm && (
        <div className="flex items-end gap-2 pl-6">
          <div>
            <label className="text-xs text-white/60 block mb-1">Weeks remaining</label>
            <input
              type="number" min="1" max="20"
              value={weeksRemaining}
              onChange={(e) => setWeeksRemaining(e.target.value)}
              className="w-20 bg-[#0a0a0a] border border-[#333] text-white rounded px-2 py-1 text-sm"
              data-testid="weeks-remaining-input"
            />
          </div>
          <span className="text-white/40 text-sm pb-1">/</span>
          <div>
            <label className="text-xs text-white/60 block mb-1">Term length</label>
            <input
              type="number" min="1" max="20"
              value={termLength}
              onChange={(e) => setTermLength(e.target.value)}
              className="w-20 bg-[#0a0a0a] border border-[#333] text-white rounded px-2 py-1 text-sm"
              data-testid="term-length-input"
            />
          </div>
          <span className="text-pink-300 text-xs pb-2 ml-2">
            = {Math.round((Number(weeksRemaining) / Number(termLength || 1)) * 100)}% of term
          </span>
        </div>
      )}

      {dirty && (
        <Button
          size="sm"
          onClick={save}
          disabled={saving}
          className="bg-pink-600 hover:bg-pink-700 h-7 text-xs"
          data-testid="save-mid-term-btn"
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      )}
      {!dirty && isMidTerm && (
        <p className="text-pink-300/70 text-xs">
          ✓ Saved: when the parent completes the link, each class will be pro-rated to {weeksRemaining}/{termLength} of full price, and the term-end date will sync with the cohort.
        </p>
      )}
    </div>
  );
};


const StudentDetail = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mailingLists, setMailingLists] = useState([]);
  const [editingPrimaryGuardian, setEditingPrimaryGuardian] = useState(false);
  const [editingGuardian2, setEditingGuardian2] = useState(false);
  const [editingLists, setEditingLists] = useState(false);
  const [editingStudentDetails, setEditingStudentDetails] = useState(false);
  const [studentDetailsForm, setStudentDetailsForm] = useState({
    name: '',
    birth_date: '',
    gender: '',
    medical_conditions: ''
  });
  const [primaryGuardianForm, setPrimaryGuardianForm] = useState({
    parent_first_name: '',
    parent_last_name: '',
    parent_email: '',
    parent_phone: ''
  });
  const [guardian2Form, setGuardian2Form] = useState({
    guardian2_first_name: '',
    guardian2_last_name: '',
    guardian2_email: '',
    guardian2_phone: '',
    guardian2_receives_emails: true
  });
  const [selectedLists, setSelectedLists] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [sendingCardLink, setSendingCardLink] = useState(false);
  const [deletingCard, setDeletingCard] = useState(null);
  const [settingDefault, setSettingDefault] = useState(null);
  
  // Add Class modal state
  const [addClassModal, setAddClassModal] = useState(false);

  // Add Bootcamp modal state
  const [addBootcampOpen, setAddBootcampOpen] = useState(false);
  const [bootcamps, setBootcamps] = useState([]);
  const [bootcampLoading, setBootcampLoading] = useState(false);
  const [bootcampForm, setBootcampForm] = useState({
    bootcamp_id: '',
    mark_as_paid: false,
    paid_amount: '',
    paid_note: '',
    // Returning-student mode (paid deposit elsewhere, owes a remaining balance,
    // email parent a Pay-now / Instalments link).
    returning_student: false,
    amount_owed: '',
    deposit_paid: '',
    admin_note: '',
    send_email: true,
  });
  const [savingBootcamp, setSavingBootcamp] = useState(false);
  const [bootcampPreview, setBootcampPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Whenever the picked bootcamp changes, fetch the price preview.
  useEffect(() => {
    if (!addBootcampOpen || !bootcampForm.bootcamp_id || !studentId) {
      setBootcampPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    api
      .get(`/bootcamps/admin/preview-price-for-student/${studentId}?bootcamp_id=${bootcampForm.bootcamp_id}`)
      .then((res) => { if (!cancelled) setBootcampPreview(res.data); })
      .catch(() => { if (!cancelled) setBootcampPreview(null); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [addBootcampOpen, bootcampForm.bootcamp_id, studentId]);

  const openAddBootcampModal = async () => {
    setAddBootcampOpen(true);
    setBootcampForm({ bootcamp_id: '', mark_as_paid: false, paid_amount: '', paid_note: '', returning_student: false, amount_owed: '', deposit_paid: '', admin_note: '', send_email: true });
    setBootcampPreview(null);
    setBootcampLoading(true);
    try {
      const res = await api.get('/bootcamps');
      setBootcamps(res.data || []);
    } catch (err) {
      toast.error('Failed to load bootcamps');
    } finally {
      setBootcampLoading(false);
    }
  };

  const submitAddBootcamp = async () => {
    if (!bootcampForm.bootcamp_id) {
      toast.error('Please pick a bootcamp');
      return;
    }
    if (bootcampForm.returning_student) {
      const owed = parseFloat(bootcampForm.amount_owed);
      if (isNaN(owed) || owed < 0) {
        toast.error('Please enter the amount still owed (£) — required for returning students');
        return;
      }
    }
    setSavingBootcamp(true);
    try {
      if (bootcampForm.returning_student) {
        const payload = {
          bootcamp_id: bootcampForm.bootcamp_id,
          student_id: studentId,
          amount_owed: parseFloat(bootcampForm.amount_owed),
          deposit_paid: bootcampForm.deposit_paid !== '' ? parseFloat(bootcampForm.deposit_paid) : undefined,
          admin_note: bootcampForm.admin_note || undefined,
          send_email: !!bootcampForm.send_email,
        };
        const res = await api.post('/bootcamps/admin/import-returning-student', payload);
        const owed = Number(res.data?.booking?.balance_owed || 0).toFixed(2);
        const camp = res.data?.booking?.bootcamp_name || 'camp';
        if (res.data?.email_sent) {
          toast.success(`${camp} reserved — parent emailed balance link (£${owed})`);
        } else if (bootcampForm.send_email) {
          toast.warning(`${camp} reserved but email failed — copy this link manually: ${res.data?.pay_url}`);
        } else {
          toast.success(`${camp} reserved — £${owed} balance pending (no email sent)`);
        }
      } else {
        const payload = {
          bootcamp_id: bootcampForm.bootcamp_id,
          mark_as_paid: !!bootcampForm.mark_as_paid,
          paid_amount: bootcampForm.paid_amount !== '' ? parseFloat(bootcampForm.paid_amount) : undefined,
          paid_note: bootcampForm.paid_note || undefined,
        };
        const res = await api.post(`/bootcamps/admin/add-to-student/${studentId}`, payload);
        const sib = res.data?.sibling_discount_info;
        const amt = Number(res.data.amount).toFixed(2);
        toast.success(
          bootcampForm.mark_as_paid
            ? `${res.data.bootcamp_name} added — recorded as paid £${amt}`
            : sib
              ? `${res.data.bootcamp_name} added at £${amt} (30% sibling discount)`
              : `${res.data.bootcamp_name} added at £${amt} — pending payment`,
        );
      }
      setAddBootcampOpen(false);
      try {
        const r = await api.get(`/students/${studentId}`);
        setStudent(r.data);
      } catch (e) { /* ignore */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add bootcamp');
    } finally {
      setSavingBootcamp(false);
    }
  };

  const [availableClasses, setAvailableClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [addingClasses, setAddingClasses] = useState(false);
  const [isProRated, setIsProRated] = useState(false);
  const [weeksRemaining, setWeeksRemaining] = useState(6);
  const [markAsPaid, setMarkAsPaid] = useState(false);
  const [activeTerm, setActiveTerm] = useState(null);
  const [removingBookingId, setRemovingBookingId] = useState(null);
  const [chargingBookingId, setChargingBookingId] = useState(null);
  const [selectedPricingOptions, setSelectedPricingOptions] = useState({}); // { classId: 'six_weeks' | 'one_off' | 'monthly' }

  // Stripe debug + force-link state
  const [stripeDebug, setStripeDebug] = useState(null);
  const [stripeDebugLoading, setStripeDebugLoading] = useState(false);
  const [manualCusId, setManualCusId] = useState('');
  const [forceLinking, setForceLinking] = useState(false);
  const [manualPmId, setManualPmId] = useState('');
  const [linkingPm, setLinkingPm] = useState(false);

  const loadStripeDebug = async () => {
    if (!student?.user_id) {
      toast.error('This student profile has no parent user_id attached');
      return;
    }
    setStripeDebugLoading(true);
    try {
      const res = await api.get(`/admin/data/user-stripe-debug/${student.user_id}`);
      setStripeDebug(res.data);
    } catch (error) {
      toast.error('Debug failed: ' + (error.response?.data?.detail || 'Unknown'));
    } finally {
      setStripeDebugLoading(false);
    }
  };

  const forceLinkCustomer = async (cusId) => {
    if (!student?.user_id) return;
    if (!confirm(`Force-link Stripe customer ${cusId} to this student's parent?`)) return;
    setForceLinking(true);
    try {
      const res = await api.post('/admin/data/force-link-stripe-customer', {
        user_id: student.user_id,
        stripe_customer_id: cusId,
      });
      const card = res.data.card;
      toast.success(
        `Linked ${cusId}` + (card ? ` — ${card.brand.toUpperCase()} ••${card.last4}` : '')
        + (res.data.bookings_fixed ? ` (${res.data.bookings_fixed} booking(s) re-stamped)` : '')
      );
      setStripeDebug(null);
      setManualCusId('');
      fetchStudent();
      if (student?.user_id) fetchPaymentMethods(student.user_id);
    } catch (error) {
      toast.error('Force-link failed: ' + (error.response?.data?.detail || 'Unknown'));
    } finally {
      setForceLinking(false);
    }
  };

  const linkPaymentMethod = async () => {
    if (!student?.user_id) return;
    const pmId = manualPmId.trim();
    if (!pmId.startsWith('pm_')) {
      toast.error('Payment method ID must start with pm_');
      return;
    }
    if (!confirm(`Link Stripe payment method ${pmId} to this student's parent?\n\nThis handles guest checkouts (gcus_...) too.`)) return;
    setLinkingPm(true);
    try {
      const res = await api.post('/admin/data/link-stripe-payment-method', {
        user_id: student.user_id,
        payment_method_id: pmId,
      });
      const card = res.data.card;
      const guestNote = res.data.is_guest_customer ? ' (guest customer)' : '';
      toast.success(
        `Linked ${pmId}${guestNote}` + (card ? ` — ${card.brand.toUpperCase()} ••${card.last4}` : '')
        + (res.data.bookings_fixed ? ` (${res.data.bookings_fixed} booking(s) re-stamped)` : '')
      );
      setManualPmId('');
      setStripeDebug(null);
      fetchStudent();
      if (student?.user_id) fetchPaymentMethods(student.user_id);
    } catch (error) {
      toast.error('Link failed: ' + (error.response?.data?.detail || 'Unknown'));
    } finally {
      setLinkingPm(false);
    }
  };
  
  // Edit pre-selected classes for unregistered students
  const [editingPreSelectedClasses, setEditingPreSelectedClasses] = useState(false);
  const [preSelectedClassIds, setPreSelectedClassIds] = useState([]);
  const [savingPreSelectedClasses, setSavingPreSelectedClasses] = useState(false);
  
  // Registration link for unregistered students
  const [registrationLink, setRegistrationLink] = useState(null);
  const [loadingRegLink, setLoadingRegLink] = useState(false);
  
  // Edit term end date
  const [editingTermEndDate, setEditingTermEndDate] = useState(null); // booking id being edited
  const [newTermEndDate, setNewTermEndDate] = useState('');
  // Inline edit-amount on pending bookings (e.g. pro-rated mid-term sibling joins)
  const [editingBookingAmount, setEditingBookingAmount] = useState(null); // booking id
  const [newBookingAmount, setNewBookingAmount] = useState('');
  const [savingBookingAmount, setSavingBookingAmount] = useState(false);

  useEffect(() => {
    fetchStudent();
    fetchMailingLists();
  }, [studentId]);
  
  // Fetch registration link when student loads (for unregistered students)
  useEffect(() => {
    const fetchRegistrationLink = async () => {
      if (student && !student.registration_complete) {
        setLoadingRegLink(true);
        try {
          const res = await api.get(`/students/${studentId}/registration-link`);
          setRegistrationLink(res.data?.registration_link || null);
        } catch (error) {
          console.log('Could not fetch registration link:', error.message);
          setRegistrationLink(null);
        } finally {
          setLoadingRegLink(false);
        }
      }
    };
    fetchRegistrationLink();
  }, [student, studentId]);

  const fetchStudent = async () => {
    try {
      const res = await api.get(`/students/${studentId}`);
      setStudent(res.data);
      // Initialize primary guardian form
      setPrimaryGuardianForm({
        parent_first_name: res.data.parent?.first_name || '',
        parent_last_name: res.data.parent?.last_name || '',
        parent_email: res.data.parent?.email || '',
        parent_phone: res.data.parent?.phone || ''
      });
      // Initialize student details form
      setStudentDetailsForm({
        name: res.data.name || res.data.student_name || '',
        birth_date: res.data.birth_date || res.data.date_of_birth || '',
        gender: res.data.gender || '',
        medical_conditions: res.data.medical_conditions || ''
      });
      // Initialize guardian2 form
      setGuardian2Form({
        guardian2_first_name: res.data.guardian2?.first_name || '',
        guardian2_last_name: res.data.guardian2?.last_name || '',
        guardian2_email: res.data.guardian2?.email || '',
        guardian2_phone: res.data.guardian2?.phone || '',
        guardian2_receives_emails: res.data.guardian2?.receives_emails ?? true
      });
      setSelectedLists(res.data.mailing_lists || []);
      // Fetch payment methods if user_id exists
      if (res.data.user_id) {
        fetchPaymentMethods(res.data.user_id);
      }
    } catch (error) {
      toast.error('Failed to load student details');
      console.error(error);
      navigate('/admin/students');
    } finally {
      setLoading(false);
    }
  };

  const fetchMailingLists = async () => {
    try {
      const res = await mailingListsAPI.getAll();
      setMailingLists(res.data.lists || []);
    } catch (error) {
      console.error('Failed to load mailing lists:', error);
    }
  };

  const fetchPaymentMethods = async (userId) => {
    if (!userId) return;
    setLoadingCards(true);
    try {
      const res = await api.get(`/payments/user/${userId}/payment-methods`);
      setPaymentMethods(res.data || []);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
    } finally {
      setLoadingCards(false);
    }
  };

  // Fetch available classes for Add Class modal
  const fetchAvailableClasses = async () => {
    setLoadingClasses(true);
    try {
      // Fetch classes and active term in parallel
      const [classesRes, termRes] = await Promise.all([
        classesAPI.getAll(),
        api.get('/terms/active')
      ]);
      
      // Filter out classes student is already enrolled in
      const enrolledClassIds = (student?.bookings || [])
        .filter(b => b.status !== 'cancelled')
        .flatMap(b => b.class_ids || [b.class_id])
        .filter(Boolean);
      
      const available = (classesRes.data || []).filter(cls => 
        !enrolledClassIds.includes(cls.id) && cls.is_visible !== false
      );
      setAvailableClasses(available);
      
      // Set active term for pro-rated calculation
      if (termRes.data) {
        setActiveTerm(termRes.data);
        // Auto-calculate weeks remaining
        const termEnd = new Date(termRes.data.end_date);
        const today = new Date();
        const weeksLeft = Math.ceil((termEnd - today) / (7 * 24 * 60 * 60 * 1000));
        setWeeksRemaining(Math.max(1, Math.min(weeksLeft, 12)));
      }
    } catch (error) {
      console.error('Failed to load classes:', error);
      toast.error('Failed to load available classes');
    } finally {
      setLoadingClasses(false);
    }
  };

  // Open Add Class modal
  const openAddClassModal = () => {
    setSelectedClassIds([]);
    setSelectedPricingOptions({});
    setIsProRated(false);
    setWeeksRemaining(6);
    setAddClassModal(true);
    fetchAvailableClasses();
  };

  // Toggle class selection
  const toggleClassSelection = (classId, cls) => {
    setSelectedClassIds(prev => {
      if (prev.includes(classId)) {
        // Removing - also remove pricing option
        setSelectedPricingOptions(opts => {
          const newOpts = { ...opts };
          delete newOpts[classId];
          return newOpts;
        });
        return prev.filter(id => id !== classId);
      } else {
        // Adding - set default pricing option to 'six_weeks'
        setSelectedPricingOptions(opts => ({
          ...opts,
          [classId]: 'six_weeks'
        }));
        return [...prev, classId];
      }
    });
  };

  // Change pricing option for a class
  const setPricingOption = (classId, option) => {
    setSelectedPricingOptions(prev => ({
      ...prev,
      [classId]: option
    }));
  };

  // Add selected classes to student
  const handleAddClasses = async () => {
    if (selectedClassIds.length === 0) {
      toast.error('Please select at least one class');
      return;
    }

    setAddingClasses(true);
    try {
      // If "Mark as already paid" is ticked, create bookings directly as 'paid'
      // with no Stripe charge. Used to record historical payments where the
      // booking record got lost but the money was collected.
      // Otherwise: 'awaiting_authorization' if parent has card, else 'pending'.
      let targetStatus;
      if (markAsPaid) {
        targetStatus = 'paid';
      } else if (student.user_id && paymentMethods.length > 0) {
        targetStatus = 'awaiting_authorization';
      } else {
        targetStatus = 'pending';
      }
      const addRes = await studentsAPI.addClasses(
        studentId, 
        selectedClassIds, 
        targetStatus,
        isProRated,
        isProRated ? weeksRemaining : null,
        selectedPricingOptions
      );
      
      if (!addRes.data?.success) {
        throw new Error(addRes.data?.message || 'Failed to add classes');
      }

      const bookingsCreated = addRes.data.bookings || [];
      const totalAmount = addRes.data.total_amount || 0;
      const weeksInfo = isProRated ? ` (${weeksRemaining} weeks)` : '';

      // Check if parent has a saved card for immediate payment
      if (markAsPaid) {
        toast.success(`Recorded ${bookingsCreated.length} class(es)${weeksInfo} as already paid — no Stripe charge.`);
      } else if (student.user_id && paymentMethods.length > 0) {
        // Try to charge immediately
        const bookingIds = bookingsCreated.map(b => b.id);
        try {
          const paymentRes = await paymentsAPI.takePayment(bookingIds);
          if (paymentRes.data?.successful?.length > 0) {
            toast.success(`Added ${bookingsCreated.length} class(es)${weeksInfo} and charged £${totalAmount.toFixed(2)} successfully!`);
          } else if (paymentRes.data?.failed?.length > 0) {
            toast.warning(`Classes added but payment failed: ${paymentRes.data.failed[0]?.error || 'Card declined'}`);
          }
        } catch (payError) {
          console.error('Payment failed:', payError);
          toast.warning(`Classes added but payment could not be processed. Payment is pending.`);
        }
      } else {
        // No saved card - classes added with pending payment status
        toast.success(`Added ${bookingsCreated.length} class(es)${weeksInfo} to ${student.name}. Payment pending (no saved card).`);
      }

      setAddClassModal(false);
      fetchStudent(); // Refresh student data
    } catch (error) {
      console.error('Failed to add classes:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to add classes');
    } finally {
      setAddingClasses(false);
    }
  };

  // Helper to get price for a class based on pricing option
  const getPriceForOption = (cls, pricingOption) => {
    switch (pricingOption) {
      case 'six_weeks':
        return cls?.six_week_price || cls?.term_price || 0;
      case 'term':
        return cls?.term_price || 0;
      case 'monthly':
        return cls?.subscription_price || 0;
      case 'one_off':
        return cls?.one_off_price || 0;
      case 'drop_in':
        return cls?.drop_in_price || 0;
      default: // 'six_weeks' as default
        return cls?.six_week_price || cls?.term_price || 0;
    }
  };

  // Calculate totals for selected classes (with pro-rating, bundle top-up, and pricing options)
  // Mirrors the server logic in routes/bookings.py add-classes endpoint so the modal
  // shows the same £ figure that will actually be charged.
  // Bundle discount applies ONLY to Saturday children's bundle classes (Bopper /
  // Junior / Senior Ballet-Tap and Street Dance) — weekday classes with "street"
  // in their name (Adult Street Fitness, Junior Street, etc) DO NOT qualify.
  const isSaturdayBundleClass = (cls) => {
    if (!cls) return false;
    const n = (cls.name || '').toLowerCase();
    const kw = n.includes('ballet') || n.includes('tap') || n.includes('street');
    // Match backend: only weekday-only classes (Mon–Fri) are excluded.
    // Saturday classes AND classes with no day_of_week both qualify.
    const day = (cls.day_of_week || '').trim().toLowerCase();
    const isWeekdayOnly = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].includes(day);
    return kw && !isWeekdayOnly;
  };
  const isSaturdayBundleByClassId = (classId) => {
    const cls = availableClasses.find(c => c.id === classId);
    return isSaturdayBundleClass(cls);
  };

  const computeBundleAdjustedTotal = (basePerClassPrices) => {
    // basePerClassPrices: array of { classId, name, price, pricingOption }
    const studentBookings = student?.bookings || [];
    const paidBundleExisting = studentBookings.filter(b =>
      b.payment_status === 'paid' &&
      b.amount &&
      b.status !== 'cancelled' &&
      isSaturdayBundleByClassId(b.class_id)
    );
    const balletKw = (n) => n.includes('ballet') || n.includes('tap');
    const streetKw = (n) => n.includes('street');
    const hasExistingBallet = paidBundleExisting.some(b => balletKw((b.class_name || '').toLowerCase()));
    const hasExistingStreet = paidBundleExisting.some(b => streetKw((b.class_name || '').toLowerCase()));
    const newBallet = basePerClassPrices.some(p => isSaturdayBundleByClassId(p.classId) && balletKw((p.name || '').toLowerCase()));
    const newStreet = basePerClassPrices.some(p => isSaturdayBundleByClassId(p.classId) && streetKw((p.name || '').toLowerCase()));
    const hasBundle = (hasExistingBallet || newBallet) && (hasExistingStreet || newStreet);

    let total = 0;
    const lineDetails = [];
    for (const item of basePerClassPrices) {
      const isBundleClass = isSaturdayBundleByClassId(item.classId);
      const isSixWeek = item.pricingOption === 'six_weeks';
      let finalPrice = item.price;
      let topUpReason = null;
      if (isSixWeek && hasBundle && isBundleClass && item.price === 54) {
        finalPrice = 45;
        // Top-up against existing paid bundle bookings
        if (paidBundleExisting.length > 0) {
          const alreadyPaid = paidBundleExisting.reduce((s, b) => s + (b.amount || 0), 0);
          const bundleTotalTarget = 45 * (paidBundleExisting.length + 1);
          finalPrice = Math.max(0, Math.round((bundleTotalTarget - alreadyPaid) * 100) / 100);
          topUpReason = `Bundle top-up: paid £${alreadyPaid.toFixed(2)} + outstanding £${finalPrice.toFixed(2)} = £${bundleTotalTarget.toFixed(2)} (2 × £45)`;
        }
      }
      total += finalPrice;
      lineDetails.push({ ...item, finalPrice, topUpReason });
    }
    return { total, lineDetails };
  };
  
  // Calculate totals for selected classes (with pro-rating and pricing options)
  const getSelectedClassesTotal = () => {
    const items = selectedClassIds.map(classId => {
      const cls = availableClasses.find(c => c.id === classId);
      const pricingOption = selectedPricingOptions[classId] || 'six_weeks';
      return { classId, name: cls?.name || '', price: getPriceForOption(cls, pricingOption), pricingOption };
    });

    // When "Mark as already paid" is ticked, skip bundle / pro-rate adjustments
    // so the admin sees (and records) the actual amount they collected.
    if (markAsPaid) {
      return items.reduce((total, it) => total + it.price, 0);
    }

    // Only apply pro-rating to 6-week bookings
    const hasSixWeekBookings = items.some(it => it.pricingOption === 'six_weeks');

    if (isProRated && weeksRemaining < 6 && hasSixWeekBookings) {
      // Pro-rate first (matches server), bundle top-up doesn't combine with pro-rate
      return items.reduce((total, it) => {
        if (it.pricingOption === 'six_weeks') {
          return total + ((it.price / 6) * weeksRemaining);
        }
        return total + it.price;
      }, 0);
    }
    // Apply bundle top-up
    return computeBundleAdjustedTotal(items).total;
  };
  
  // Get full price (without pro-rating)
  const getFullTermPrice = () => {
    return selectedClassIds.reduce((total, classId) => {
      const cls = availableClasses.find(c => c.id === classId);
      const pricingOption = selectedPricingOptions[classId] || 'six_weeks';
      return total + getPriceForOption(cls, pricingOption);
    }, 0);
  };

  // Remove/cancel a booking
  const handleRemoveBooking = async (bookingId, className) => {
    if (!window.confirm(`Are you sure you want to remove "${className}" from ${student?.name}'s bookings?\n\nThis will cancel the booking.`)) {
      return;
    }
    
    setRemovingBookingId(bookingId);
    try {
      await api.delete(`/bookings/${bookingId}`);
      toast.success(`Removed "${className}" from ${student?.name}'s bookings`);
      fetchStudent(); // Refresh student data
    } catch (error) {
      console.error('Failed to remove booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove booking');
    } finally {
      setRemovingBookingId(null);
    }
  };

  const handleChargeBooking = async (bookingId, className, amount) => {
    if (!student.user_id) {
      toast.error('Parent is not registered — cannot take payment');
      return;
    }
    if (!paymentMethods || paymentMethods.length === 0) {
      toast.error('No saved card on file. Send the parent a "Add card" link first.');
      return;
    }
    if (!window.confirm(
      `Charge ${formatPrice(amount)} to ${student?.name}'s saved card for "${className}"?`
    )) {
      return;
    }

    setChargingBookingId(bookingId);
    try {
      const res = await paymentsAPI.takePayment([bookingId]);
      // Backend returns: { total_processed, successful (count), failed (count), results: [{success, error?, amount_charged?, ...}] }
      const results = res.data?.results || [];
      const result = results[0] || {};
      const succeededCount = typeof res.data?.successful === 'number' ? res.data.successful : results.filter(r => r.success).length;
      if (succeededCount > 0 && result.success) {
        toast.success(`Charged ${formatPrice(result.amount_charged || amount)} for "${className}"`);
      } else {
        const reason = result.error || res.data?.detail || 'No details returned by Stripe — check the Stripe Dashboard.';
        toast.error(`Payment failed: ${reason}`, { duration: 12000 });
        // eslint-disable-next-line no-alert
        alert(
          `Payment failed\n\n` +
          `Reason from backend: ${reason}\n\n` +
          `Booking: ${bookingId}\n` +
          `Amount: ${formatPrice(amount)}\n\n` +
          `Copy this message and share it if you need help debugging.`
        );
      }
      fetchStudent();
    } catch (error) {
      console.error('Failed to charge booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to take payment');
    } finally {
      setChargingBookingId(null);
    }
  };

  const handleUpdateTermEndDate = async (bookingId) => {
    if (!newTermEndDate) {
      toast.error('Please select a date');
      return;
    }
    try {
      await api.patch(`/bookings/${bookingId}/term-end-date?term_end_date=${newTermEndDate}`);
      toast.success('Term end date updated - student will now appear in Renewals');
      setEditingTermEndDate(null);
      setNewTermEndDate('');
      fetchStudent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update term end date');
    }
  };

  const handleSaveBookingAmount = async (bookingId) => {
    const amt = parseFloat(newBookingAmount);
    if (isNaN(amt) || amt < 0) {
      toast.error('Enter a valid amount (£0 or more)');
      return;
    }
    setSavingBookingAmount(true);
    try {
      await api.patch(`/bookings/admin/${bookingId}/amount?amount=${amt}`);
      toast.success(`Amount updated to £${amt.toFixed(2)}`);
      setEditingBookingAmount(null);
      setNewBookingAmount('');
      fetchStudent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update amount');
    } finally {
      setSavingBookingAmount(false);
    }
  };

  const handleSendCardLink = async () => {
    if (!student?.parent_email) {
      toast.error('No parent email available');
      return;
    }
    setSendingCardLink(true);
    try {
      await api.post(`/payments/send-card-link`, { 
        email: student.parent_email,
        parent_name: student.parent_name || 'Parent'
      });
      toast.success('Card setup link sent to parent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send card link');
    } finally {
      setSendingCardLink(false);
    }
  };

  const handleCopyCardLink = async () => {
    if (!student?.parent_email) {
      toast.error('No parent email available');
      return;
    }
    setSendingCardLink(true);
    try {
      const res = await api.post(`/payments/send-card-link`, {
        email: student.parent_email,
        parent_name: student.parent_name || 'Parent',
        send_email: false,
      });
      const url = res.data?.link;
      if (!url) {
        toast.error('No link returned by server');
        return;
      }
      const ok = await copyToClipboard(url);
      if (ok) {
        toast.success('Card setup link copied — paste into WhatsApp');
      }
      // Always show modal too, so admin can grab the link if clipboard failed
      setLinkModal({
        title: 'Card Setup Link',
        message: `Send this to ${student.parent_name || 'the parent'} via WhatsApp / SMS. The link expires in 24 hours.`,
        url,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate link');
    } finally {
      setSendingCardLink(false);
    }
  };

  // Modal that surfaces a generated link (password reset, registration invite, etc.)
  // because navigator.clipboard.writeText is unreliable in Safari/iOS.
  const [linkModal, setLinkModal] = useState(null); // { title, message, url } | null

  // Scholarship / discount editor modal
  const [discountModal, setDiscountModal] = useState(null); // { percent, note } | null
  const [savingDiscount, setSavingDiscount] = useState(false);

  const openDiscountModal = () => {
    setDiscountModal({
      percent: student?.discount_percent || 0,
      note: student?.discount_note || '',
    });
  };

  const saveDiscount = async () => {
    if (!discountModal) return;
    const pct = Math.min(100, Math.max(0, Number(discountModal.percent) || 0));
    setSavingDiscount(true);
    try {
      await api.put(`/students/${student.id}/discount`, {
        discount_percent: pct,
        discount_note: (discountModal.note || '').trim(),
      });
      toast.success(pct > 0 ? `${pct}% scholarship saved` : 'Scholarship removed');
      setDiscountModal(null);
      fetchStudent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save scholarship');
    } finally {
      setSavingDiscount(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fallback: use deprecated execCommand which works in more browsers
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
      } catch { return false; }
    }
  };

  const handleSendPasswordReset = async (alsoEmail = true) => {
    const parentEmail = student?.parent?.email || student?.parent_email;
    if (!parentEmail) {
      toast.error('No parent email available');
      return;
    }
    try {
      const res = await api.post('/auth/admin/trigger-password-reset', {
        email: parentEmail,
        send_email: alsoEmail,
      });
      const url = res.data.reset_url;
      const copied = await copyToClipboard(url);
      const sent = alsoEmail && res.data.email_sent;
      setLinkModal({
        title: sent ? 'Reset email sent ✓' : 'Reset link generated',
        message: sent
          ? `Email sent to ${parentEmail}. If they don't receive it, share this link via SMS/WhatsApp:`
          : alsoEmail && !res.data.email_sent
            ? `Email failed (${res.data.email_error || 'unknown'}). Share this link via SMS/WhatsApp instead:`
            : 'Share this link with the parent via SMS/WhatsApp:',
        url,
        copied,
      });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to generate reset link';
      if (error.response?.status === 404 || /no.*account/i.test(detail)) {
        if (window.confirm(
          `${detail}\n\nCreate a parent login for ${parentEmail} now? This will:\n• Create a parent account\n• Link any existing bookings/children to it\n• Send them a "Set Password" email (and show the link).`,
        )) {
          await handleCreateLoginForParent();
        }
      } else {
        toast.error(detail);
      }
    }
  };

  const handleCreateLoginForParent = async () => {
    const parentEmail = student?.parent?.email || student?.parent_email;
    const parentName = student?.parent?.name || student?.parent_name || '';
    if (!parentEmail) {
      toast.error('No parent email available');
      return;
    }
    const [first, ...rest] = parentName.split(' ');
    try {
      const res = await api.post('/auth/admin/create-login-for-parent', {
        email: parentEmail,
        first_name: first || undefined,
        last_name: rest.join(' ') || undefined,
        phone: student?.parent?.phone || undefined,
        send_email: true,
      });
      const url = res.data.reset_url;
      const copied = await copyToClipboard(url);
      const linkedSummary = `Linked ${res.data.linked.bookings} bookings, ${res.data.linked.bootcamp_bookings} camps, ${res.data.linked.children} children.`;
      setLinkModal({
        title: res.data.email_sent ? 'Account created — email sent ✓' : 'Account created',
        message: res.data.email_sent
          ? `Welcome email sent to ${parentEmail}. ${linkedSummary} If it doesn't arrive, share this link via SMS/WhatsApp:`
          : `Email failed (${res.data.email_error || 'unknown'}). Share this set-password link via SMS/WhatsApp. ${linkedSummary}`,
        url,
        copied,
      });
      fetchStudent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create login');
    }
  };

  const handleDeleteCard = async (paymentMethodId) => {
    if (!window.confirm('Are you sure you want to delete this card?')) return;
    setDeletingCard(paymentMethodId);
    try {
      await api.delete(`/payments/user/${student.user_id}/payment-methods/${paymentMethodId}`);
      toast.success('Card deleted');
      fetchPaymentMethods(student.user_id);
    } catch (error) {
      toast.error('Failed to delete card');
    } finally {
      setDeletingCard(null);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    setSettingDefault(paymentMethodId);
    try {
      await api.post(`/payments/user/${student.user_id}/payment-methods/${paymentMethodId}/set-default`);
      toast.success('Default card updated');
      fetchPaymentMethods(student.user_id);
    } catch (error) {
      toast.error('Failed to set default card');
    } finally {
      setSettingDefault(null);
    }
  };

  const resendInvite = async () => {
    try {
      await api.post(`/students/${studentId}/resend-invite`);
      toast.success('Invitation email sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    }
  };

  const copyRegistrationLink = async () => {
    try {
      const res = await api.get(`/students/${studentId}/registration-link`);
      const link = res.data?.registration_link;
      if (link) {
        await navigator.clipboard.writeText(link);
        toast.success('Registration link copied to clipboard! You can send it manually to the parent.');
      } else {
        toast.error('No registration link available');
      }
    } catch (error) {
      console.error('Copy link error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        code: error.code
      });
      
      // Handle different error types
      if (error.code === 'ERR_NETWORK' || !error.response) {
        toast.error('Network error - please check your connection and try again');
      } else if (error.response?.status === 401) {
        toast.error('Session expired - please log in again');
      } else if (error.response?.status === 404) {
        toast.error('Student not found in pending registrations');
      } else {
        const errorDetail = error.response?.data?.detail || 'Failed to get registration link';
        toast.error(errorDetail);
      }
    }
  };

  const savePrimaryGuardian = async () => {
    if (!primaryGuardianForm.parent_email) {
      toast.error('Email is required');
      return;
    }
    setSaving(true);
    try {
      await studentsAPI.update(studentId, primaryGuardianForm);
      toast.success('Primary guardian updated');
      setEditingPrimaryGuardian(false);
      fetchStudent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const saveStudentDetails = async () => {
    if (!studentDetailsForm.name?.trim()) {
      toast.error('Student name is required');
      return;
    }
    setSaving(true);
    try {
      await studentsAPI.update(studentId, studentDetailsForm);
      toast.success('Student details updated');
      setEditingStudentDetails(false);
      fetchStudent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const saveGuardian2 = async () => {
    setSaving(true);
    try {
      await studentsAPI.update(studentId, guardian2Form);
      toast.success('Second guardian updated');
      setEditingGuardian2(false);
      fetchStudent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const saveMailingLists = async () => {
    setSaving(true);
    try {
      await studentsAPI.updateMailingLists(studentId, selectedLists);
      toast.success('Mailing lists updated');
      setEditingLists(false);
      fetchStudent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const openEditPreSelectedClasses = async () => {
    // Load available classes and current pre-selected classes
    setLoadingClasses(true);
    try {
      const classesRes = await classesAPI.getAll();
      setAvailableClasses(classesRes.data || []);
      // Get current pre-selected class IDs from the student
      const currentClassIds = student.pre_selected_class_ids || student.class_ids || [];
      setPreSelectedClassIds(currentClassIds);
      setEditingPreSelectedClasses(true);
    } catch (error) {
      toast.error('Failed to load classes');
    } finally {
      setLoadingClasses(false);
    }
  };

  const togglePreSelectedClass = (classId) => {
    setPreSelectedClassIds(prev => 
      prev.includes(classId)
        ? prev.filter(id => id !== classId)
        : [...prev, classId]
    );
  };

  const savePreSelectedClasses = async () => {
    setSavingPreSelectedClasses(true);
    try {
      const res = await api.put(`/students/${studentId}/pre-selected-classes`, {
        class_ids: preSelectedClassIds
      });
      const bookingsUpdated = res.data?.bookings_updated || 0;
      toast.success(
        bookingsUpdated > 0
          ? `Classes updated and ${bookingsUpdated} booking(s) recalculated with correct pricing!`
          : 'Pre-selected classes updated!'
      );
      setEditingPreSelectedClasses(false);
      fetchStudent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update classes');
    } finally {
      setSavingPreSelectedClasses(false);
    }
  };

  const syncBookings = async () => {
    try {
      const res = await api.post(`/students/${studentId}/sync-bookings`);
      toast.success(res.data?.message || 'Bookings synced with current classes');
      fetchStudent();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync bookings');
    }
  };


  const toggleList = (listName) => {
    setSelectedLists(prev => 
      prev.includes(listName)
        ? prev.filter(l => l !== listName)
        : [...prev, listName]
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not provided';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatPrice = (amount) => {
    return `£${(amount || 0).toFixed(2)}`;
  };

  const getPaymentStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle className="w-3 h-3 mr-1" /> Paid</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case 'free_enrollment':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30"><Tag className="w-3 h-3 mr-1" /> Free (100% Discount)</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">{status || 'Unknown'}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF007F]"></div>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-white/50">Student not found</p>
        </div>
      </div>
    );
  }

  const totalPaid = student.bookings?.reduce((sum, b) => b.payment_status === 'paid' ? sum + (b.amount || 0) : sum, 0) || 0;
  const totalPending = student.bookings?.reduce((sum, b) => b.payment_status === 'pending' ? sum + (b.amount || 0) : sum, 0) || 0;
  const hasGuardian2 = guardian2Form.guardian2_first_name || guardian2Form.guardian2_email;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      <Toaster position="top-right" richColors />
      
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin/students')}
            className="text-white/70 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Students
          </Button>
          
          {!student.registration_complete && (
            <div className="flex flex-col gap-2 items-end">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={copyRegistrationLink}
                  className="border-[#FF007F] text-[#FF007F] hover:bg-[#FF007F]/10"
                  title="Copy the registration link to send manually"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Button
                  onClick={resendInvite}
                  className="bg-[#FF007F] hover:bg-[#D6006A]"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Resend Invite
                </Button>
              </div>
              {/* Show registration link directly if available */}
              {registrationLink && (
                <div className="text-xs text-white/50 max-w-md">
                  <span className="text-white/30">Link: </span>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(registrationLink);
                      toast.success('Link copied!');
                    }}
                    className="text-[#FF007F] hover:underline break-all text-left"
                    title="Click to copy"
                  >
                    {registrationLink}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Student Header */}
        <div className="flex items-start gap-6 mb-8">
          <div className="w-20 h-20 rounded-full bg-[#FF007F]/20 flex items-center justify-center">
            <span className="text-[#FF007F] font-bold text-3xl">
              {student.name?.charAt(0)?.toUpperCase() || '?'}
            </span>
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-white mb-2">{student.name}</h1>
            <div className="flex items-center gap-3 flex-wrap">
              {!student.registration_complete ? (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Registration {student.registration_status === 'invite_pending' ? 'Invite Sent' : 'Pending'}
                </Badge>
              ) : (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Registered
                </Badge>
              )}
              {student.discount_percent > 0 && (
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                  <Tag className="w-3 h-3 mr-1" />
                  {student.discount_percent}% Discount
                </Badge>
              )}
              {(student.mailing_lists || []).map(list => (
                <Badge key={list} className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                  {list}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        <Tabs defaultValue="details" className="space-y-6">
          <TabsList className="bg-[#111] border border-[#333]">
            <TabsTrigger value="details" className="data-[state=active]:bg-[#FF007F]">
              <User className="w-4 h-4 mr-2" />
              Details
            </TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-[#FF007F]">
              <BookOpen className="w-4 h-4 mr-2" />
              Bookings
            </TabsTrigger>
            <TabsTrigger value="billing" className="data-[state=active]:bg-[#FF007F]">
              <CreditCard className="w-4 h-4 mr-2" />
              Billing
            </TabsTrigger>
            <TabsTrigger value="attendance" className="data-[state=active]:bg-[#FF007F]">
              <History className="w-4 h-4 mr-2" />
              Attendance
            </TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Student Info */}
              <Card className="bg-[#111] border-[#333]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <User className="w-5 h-5 text-[#FF007F]" />
                    Student Information
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingStudentDetails(true)}
                    className="text-white/50 hover:text-white"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-white/50 text-sm">Full Name</p>
                    <p className="text-white font-medium">{student.name || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Date of Birth</p>
                    <p className="text-white font-medium">{formatDate(student.birth_date)}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Gender</p>
                    <p className="text-white font-medium">{student.gender || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Registered</p>
                    <p className="text-white font-medium">{formatDate(student.created_at)}</p>
                  </div>
                  {/* Scholarship / discount editor */}
                  <div className={`p-3 rounded-lg border ${student.discount_percent > 0 ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-[#0a0a0a] border-[#333]'}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        {student.discount_percent > 0 ? (
                          <>
                            <p className="text-yellow-400 font-medium" data-testid="discount-badge">
                              {student.discount_percent}% Scholarship / Discount
                            </p>
                            {student.discount_note && (
                              <p className="text-yellow-400/70 text-sm">{student.discount_note}</p>
                            )}
                          </>
                        ) : (
                          <p className="text-white/60 text-sm">No scholarship / discount set</p>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={openDiscountModal}
                        className="border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10"
                        data-testid="edit-discount-btn"
                      >
                        <Edit className="w-3 h-3 mr-1" /> {student.discount_percent > 0 ? 'Edit' : 'Add scholarship'}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Pre-selected Classes (for unregistered students) */}
                  {!student.registration_complete && (student.class_ids?.length > 0 || student.pre_selected_class_ids?.length > 0) && (
                    <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-purple-400 font-medium flex items-center gap-2">
                          <BookOpen className="w-4 h-4" />
                          Pre-selected Classes
                        </p>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={openEditPreSelectedClasses}
                            className="text-purple-400 hover:text-purple-300 h-6 px-2"
                          >
                            <Edit className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={syncBookings}
                            className="text-amber-400 hover:text-amber-300 h-6 px-2"
                            title="Recalculate bookings to match these classes"
                            data-testid="sync-bookings-btn"
                          >
                            <RefreshCw className="w-3 h-3 mr-1" /> Sync Payments
                          </Button>
                        </div>
                      </div>
                      <ul className="space-y-1">
                        {(student.class_names || []).map((name, idx) => (
                          <li key={idx} className="text-white/80 text-sm flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                            {name}
                          </li>
                        ))}
                      </ul>
                      <p className="text-purple-400/60 text-xs mt-2">
                        These classes will be shown when the parent registers. You can edit them before registration.
                      </p>

                      {/* Mid-term joining toggle */}
                      <div className="mt-3 pt-3 border-t border-purple-500/20">
                        <MidTermToggle
                          student={student}
                          onSaved={fetchStudent}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Primary Guardian Info */}
              <Card className="bg-[#111] border-[#333]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <User className="w-5 h-5 text-[#7000FF]" />
                    Primary Guardian
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingPrimaryGuardian(true)}
                    className="text-white/50 hover:text-white"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-white/50 text-sm">Name</p>
                    <p className="text-white font-medium">
                      {student.parent?.first_name || student.parent?.last_name 
                        ? `${student.parent.first_name} ${student.parent.last_name}`.trim()
                        : 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm flex items-center gap-1">
                      <Mail className="w-3 h-3" /> Email
                    </p>
                    <p className="text-white font-medium">
                      {student.parent?.email ? (
                        <a href={`mailto:${student.parent.email}`} className="text-[#FF007F] hover:underline">
                          {student.parent.email}
                        </a>
                      ) : 'Not provided'}
                    </p>
                  </div>
                  <div>
                    <p className="text-white/50 text-sm flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Phone
                    </p>
                    <p className="text-white font-medium">
                      {student.parent?.phone ? (
                        <a href={`tel:${student.parent.phone}`} className="text-[#FF007F] hover:underline">
                          {student.parent.phone}
                        </a>
                      ) : 'Not provided'}
                    </p>
                  </div>

                  {/* Password reset tools — for when a parent says they can't log in */}
                  {student.parent?.email && (
                    <div className="pt-3 border-t border-[#222] space-y-2">
                      <p className="text-white/50 text-xs uppercase tracking-wider">Login Help</p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendPasswordReset(true)}
                          className="border-pink-500/40 text-pink-400 hover:bg-pink-500/10 text-xs"
                          data-testid="send-password-reset-btn"
                        >
                          <Mail className="w-3 h-3 mr-1" /> Send password reset email
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendPasswordReset(false)}
                          className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 text-xs"
                          data-testid="copy-reset-link-btn"
                        >
                          <Link2 className="w-3 h-3 mr-1" /> Copy reset link
                        </Button>
                      </div>
                      <p className="text-xs text-white/40">
                        💡 If the email doesn't arrive, use "Copy reset link" and send it via WhatsApp/SMS.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Second Guardian Info */}
              <Card className="bg-[#111] border-[#333]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    Second Guardian
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingGuardian2(true)}
                    className="text-white/50 hover:text-white"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                  {hasGuardian2 ? (
                    <>
                      <div>
                        <p className="text-white/50 text-sm">Name</p>
                        <p className="text-white font-medium">
                          {`${guardian2Form.guardian2_first_name} ${guardian2Form.guardian2_last_name}`.trim() || 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/50 text-sm flex items-center gap-1">
                          <Mail className="w-3 h-3" /> Email
                        </p>
                        <p className="text-white font-medium">
                          {guardian2Form.guardian2_email ? (
                            <a href={`mailto:${guardian2Form.guardian2_email}`} className="text-[#FF007F] hover:underline">
                              {guardian2Form.guardian2_email}
                            </a>
                          ) : 'Not provided'}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/50 text-sm flex items-center gap-1">
                          <Phone className="w-3 h-3" /> Phone
                        </p>
                        <p className="text-white font-medium">
                          {guardian2Form.guardian2_phone || 'Not provided'}
                        </p>
                      </div>
                      <div className="pt-2 border-t border-[#333]">
                        <Badge className={guardian2Form.guardian2_receives_emails ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}>
                          {guardian2Form.guardian2_receives_emails ? 'Receives all emails' : 'Does not receive emails'}
                        </Badge>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <Users className="w-8 h-8 text-white/20 mx-auto mb-2" />
                      <p className="text-white/50 text-sm">No second guardian added</p>
                      <Button
                        size="sm"
                        onClick={() => setEditingGuardian2(true)}
                        className="mt-2 bg-[#FF007F] hover:bg-[#D6006A]"
                      >
                        Add Second Guardian
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Mailing Lists */}
              <Card className="bg-[#111] border-[#333]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Mail className="w-5 h-5 text-purple-400" />
                    Mailing Lists
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingLists(true)}
                    className="text-white/50 hover:text-white"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </CardHeader>
                <CardContent>
                  {(student.mailing_lists || []).length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {student.mailing_lists.map(list => (
                        <Badge key={list} className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                          {list}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-white/50 text-sm">Not assigned to any mailing lists</p>
                  )}
                </CardContent>
              </Card>

              {/* Medical Info */}
              <Card className="bg-[#111] border-[#333] md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Heart className="w-5 h-5 text-red-400" />
                    Medical Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                    <p className="text-white">
                      {student.medical_conditions || 'No medical conditions recorded'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            {/* Payment Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card className="bg-[#111] border-[#333]">
                <CardContent className="p-4">
                  <p className="text-white/50 text-sm">Total Paid</p>
                  <p className="text-2xl font-bold text-green-400">{formatPrice(totalPaid)}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#111] border-[#333]">
                <CardContent className="p-4">
                  <p className="text-white/50 text-sm">Pending Payment</p>
                  <p className="text-2xl font-bold text-yellow-400">{formatPrice(totalPending)}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#111] border-[#333]">
                <CardContent className="p-4">
                  <p className="text-white/50 text-sm">Total Bookings</p>
                  <p className="text-2xl font-bold text-[#FF007F]">{student.bookings?.length || 0}</p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-[#111] border-[#333]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#FF007F]" />
                    Booking History
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={syncBookings}
                      className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                      title="Recalculate bookings from pre-selected classes with correct pricing"
                      data-testid="sync-bookings-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Recalculate
                    </Button>
                    <Button
                      onClick={openAddClassModal}
                      className="bg-[#FF007F] hover:bg-[#D6006A]"
                      data-testid="add-class-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Class
                    </Button>
                    <Button
                      onClick={openAddBootcampModal}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="add-bootcamp-btn"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Add Bootcamp
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!student.bookings || student.bookings.length === 0 ? (
                  <div className="text-center py-8">
                    <BookOpen className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">No bookings yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {student.bookings.map((booking, idx) => (
                      <div 
                        key={booking.id || idx}
                        className="p-4 rounded-lg bg-[#1a1a1a] border border-[#333]"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-white font-semibold">
                              {booking.class_names?.join(', ') || booking.class_name || 'Unknown Class'}
                            </h4>
                            <p className="text-white/50 text-sm">
                              {booking.booking_type === 'term_enrollment' ? 'Term Enrollment' : 
                               booking.booking_type === 'class_addition' ? 'Added Class' : 
                               booking.booking_type || 'Booking'}
                              {booking.is_bundle && ' (Bundle)'}
                              {booking.is_pro_rated && ` (${booking.weeks_remaining} weeks)`}
                            </p>
                            <p className="text-white/30 text-xs mt-1">
                              {formatDate(booking.created_at)}
                            </p>
                            {/* Term End Date — editable for pending OR paid bookings.
                                Pro-rated mid-term joins need term-end set to the cohort end
                                BEFORE the parent pays so renewals fire on time. */}
                            {booking.status !== 'cancelled' && (
                              <div className="mt-2 flex items-center gap-2">
                                {editingTermEndDate === booking.id ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="date"
                                      value={newTermEndDate}
                                      onChange={(e) => setNewTermEndDate(e.target.value)}
                                      className="bg-[#222] border border-[#444] rounded px-2 py-1 text-sm text-white"
                                    />
                                    <Button
                                      size="sm"
                                      onClick={() => handleUpdateTermEndDate(booking.id)}
                                      className="h-7 px-2 bg-green-600 hover:bg-green-700"
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setEditingTermEndDate(null);
                                        setNewTermEndDate('');
                                      }}
                                      className="h-7 px-2 text-white/50 hover:text-white"
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <span className={`text-xs ${
                                      booking.term_end_date && new Date(booking.term_end_date) < new Date() 
                                        ? 'text-red-400' 
                                        : 'text-white/50'
                                    }`}>
                                      Term ends: {booking.term_end_date 
                                        ? new Date(booking.term_end_date).toLocaleDateString() 
                                        : 'Not set'}
                                    </span>
                                    <button
                                      onClick={() => {
                                        setEditingTermEndDate(booking.id);
                                        setNewTermEndDate(booking.term_end_date 
                                          ? booking.term_end_date.split('T')[0] 
                                          : '');
                                      }}
                                      className="text-[#FF007F] hover:text-[#FF007F]/80 text-xs"
                                      title="Edit term end date"
                                    >
                                      <Edit className="w-3 h-3 inline" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="text-right flex items-start gap-2">
                            <div>
                              {editingBookingAmount === booking.id ? (
                                <div className="flex items-center gap-1">
                                  <span className="text-white font-bold">£</span>
                                  <input
                                    type="number" step="0.01" min="0"
                                    value={newBookingAmount}
                                    onChange={(e) => setNewBookingAmount(e.target.value)}
                                    className="w-20 bg-[#222] border border-[#444] rounded px-2 py-1 text-sm text-white"
                                    autoFocus
                                  />
                                  <Button
                                    size="sm"
                                    onClick={() => handleSaveBookingAmount(booking.id)}
                                    disabled={savingBookingAmount}
                                    className="h-7 px-2 bg-green-600 hover:bg-green-700 text-xs"
                                    data-testid={`save-amount-${booking.id}`}
                                  >
                                    {savingBookingAmount ? '...' : 'Save'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => { setEditingBookingAmount(null); setNewBookingAmount(''); }}
                                    className="h-7 px-2 text-white/50 hover:text-white text-xs"
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 justify-end">
                                  <p className="text-white font-bold">{formatPrice(booking.amount)}</p>
                                  {booking.status !== 'cancelled' && booking.payment_status !== 'paid' && (
                                    <button
                                      onClick={() => {
                                        setEditingBookingAmount(booking.id);
                                        setNewBookingAmount(String(booking.amount ?? ''));
                                      }}
                                      className="text-[#FF007F] hover:text-[#FF007F]/80"
                                      title="Edit amount (e.g. pro-rated mid-term join)"
                                      data-testid={`edit-amount-${booking.id}`}
                                    >
                                      <Edit className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              )}
                              {booking.original_amount_before_manual_edit && (
                                <p className="text-white/40 text-xs line-through text-right">
                                  was {formatPrice(booking.original_amount_before_manual_edit)}
                                </p>
                              )}
                              {booking.discount_applied > 0 && (
                                <p className="text-green-400 text-xs">-{formatPrice(booking.discount_applied)} discount</p>
                              )}
                              <div className="mt-2">
                                {getPaymentStatusBadge(booking.payment_status)}
                              </div>
                              {/* Pay Now — for pending / awaiting_authorization bookings on registered parents */}
                              {booking.status !== 'cancelled' &&
                                booking.payment_status !== 'paid' &&
                                booking.payment_status !== 'free_enrollment' &&
                                student.user_id &&
                                paymentMethods.length > 0 && (
                                <Button
                                  size="sm"
                                  onClick={() => handleChargeBooking(
                                    booking.id,
                                    booking.class_names?.join(', ') || booking.class_name || 'this class',
                                    booking.amount || 0
                                  )}
                                  disabled={chargingBookingId === booking.id}
                                  className="mt-2 h-7 px-3 bg-emerald-500 hover:bg-emerald-600 text-white text-xs"
                                  data-testid={`pay-now-${booking.id}`}
                                >
                                  {chargingBookingId === booking.id ? (
                                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Charging…</>
                                  ) : (
                                    <><CreditCard className="w-3 h-3 mr-1" /> Pay Now</>
                                  )}
                                </Button>
                              )}
                            </div>
                            {booking.status !== 'cancelled' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveBooking(
                                  booking.id, 
                                  booking.class_names?.join(', ') || booking.class_name || 'this class'
                                )}
                                disabled={removingBookingId === booking.id}
                                className="text-red-400 hover:text-red-300 hover:bg-red-400/10 p-2"
                                title="Remove this class"
                                data-testid={`remove-booking-${booking.id}`}
                              >
                                {removingBookingId === booking.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                        {booking.status === 'cancelled' && (
                          <Badge variant="outline" className="mt-2 border-red-500/50 text-red-400">
                            Cancelled
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <Card className="bg-[#111] border-[#333]">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-emerald-400" />
                      Billing Information
                    </CardTitle>
                    <p className="text-white/60 text-sm mt-1">
                      You can add multiple cards including cards for different locations, and switch between them when making bookings.
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      onClick={handleSendCardLink}
                      disabled={sendingCardLink}
                      variant="outline"
                      className="border-pink-500/30 text-pink-400 hover:bg-pink-500/10"
                      data-testid="send-card-link-btn"
                    >
                      {sendingCardLink ? 'Working...' : (
                        <>
                          <Link2 className="w-4 h-4 mr-2" />
                          Send "Add card" link
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={handleCopyCardLink}
                      disabled={sendingCardLink}
                      variant="outline"
                      className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                      data-testid="copy-card-link-btn"
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      Copy card link
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Debug & Force-link (admin-only repair tool) */}
                {student.user_id && (
                  <div className="mb-4 p-3 bg-[#0a0a0a] border border-amber-500/20 rounded-lg">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <p className="text-xs text-amber-400 font-semibold">⚠️ Wrong card showing? Stripe Repair Tool</p>
                        <p className="text-xs text-white/40 mt-0.5">
                          user_id: <span className="font-mono">{student.user_id.slice(0, 8)}…</span>
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={loadStripeDebug}
                        disabled={stripeDebugLoading}
                        className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 h-7 px-3 text-xs"
                        data-testid="stripe-debug-btn"
                      >
                        {stripeDebugLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Show Stripe customers for this parent'}
                      </Button>
                    </div>
                    {stripeDebug && (
                      <div className="mt-3 space-y-2 text-xs">
                        <div className="text-white/60">
                          DB has: <span className="font-mono text-white">{stripeDebug.user.stripe_customer_id_in_db || '— none —'}</span>
                        </div>
                        {stripeDebug.stripe_customers_by_email.length === 0 && (
                          <div className="text-white/50 italic">No Stripe customers found for {stripeDebug.user.email}</div>
                        )}
                        {stripeDebug.stripe_customers_by_email.map((c) => (
                          <div key={c.id} className={`p-2 rounded border ${c.matches_db ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-[#333]'}`}>
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div>
                                <div className="text-white font-mono">{c.id} {c.matches_db && <span className="text-emerald-400 ml-2">✓ current</span>}</div>
                                <div className="text-white/60 text-xs">{c.name || '—'} · {c.email}</div>
                                <div className="text-white/70 mt-1">
                                  {c.cards.length === 0 ? <span className="italic text-white/40">no cards</span> : c.cards.map(card => (
                                    <span key={card.pm_id} className="mr-3">{card.brand.toUpperCase()} ••{card.last4} ({card.exp})</span>
                                  ))}
                                </div>
                              </div>
                              {!c.matches_db && c.cards.length > 0 && (
                                <Button
                                  size="sm"
                                  onClick={() => forceLinkCustomer(c.id)}
                                  disabled={forceLinking}
                                  className="h-6 px-2 text-xs bg-amber-600 hover:bg-amber-700"
                                  data-testid={`force-link-${c.id}`}
                                >
                                  Use this one
                                </Button>
                              )}
                            </div>
                            {c.recent_payments.length > 0 && (
                              <div className="text-white/40 text-xs mt-1">
                                Recent: {c.recent_payments.slice(0, 3).map(p => `£${p.amount.toFixed(2)}`).join(', ')}
                              </div>
                            )}
                          </div>
                        ))}
                        {/* Manual customer ID input */}
                        <div className="flex gap-2 pt-2 border-t border-[#222]">
                          <input
                            type="text"
                            value={manualCusId}
                            onChange={(e) => setManualCusId(e.target.value)}
                            placeholder="Or paste any cus_... or gcus_... (guest checkout)"
                            className="flex-1 bg-[#0a0a0a] border border-[#333] rounded px-2 py-1 text-white text-xs font-mono placeholder:text-white/20"
                            data-testid="manual-cus-id-input"
                          />
                          <Button
                            size="sm"
                            onClick={() => forceLinkCustomer(manualCusId.trim())}
                            disabled={
                              forceLinking ||
                              !(manualCusId.trim().startsWith('cus_') || manualCusId.trim().startsWith('gcus_'))
                            }
                            className="h-7 px-3 text-xs bg-amber-600 hover:bg-amber-700"
                            data-testid="manual-force-link-btn"
                          >
                            {forceLinking ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Force-link'}
                          </Button>
                        </div>
                        {/* Direct pm_ link — for guest checkouts where cus_/gcus_ isn't known */}
                        <div className="pt-2 border-t border-[#222]">
                          <p className="text-xs text-amber-300/80 mb-1">
                            Guest checkout? Paste the <span className="font-mono">pm_…</span> from the Stripe payment page (Payment method → ID).
                          </p>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={manualPmId}
                              onChange={(e) => setManualPmId(e.target.value)}
                              placeholder="pm_1TJSfh..."
                              className="flex-1 bg-[#0a0a0a] border border-[#333] rounded px-2 py-1 text-white text-xs font-mono placeholder:text-white/20"
                              data-testid="manual-pm-id-input"
                            />
                            <Button
                              size="sm"
                              onClick={linkPaymentMethod}
                              disabled={linkingPm || !manualPmId.trim().startsWith('pm_')}
                              className="h-7 px-3 text-xs bg-emerald-600 hover:bg-emerald-700"
                              data-testid="link-pm-btn"
                            >
                              {linkingPm ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Link card'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {loadingCards ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
                  </div>
                ) : !student.user_id ? (
                  <div className="text-center py-12">
                    <AlertCircle className="w-16 h-16 mx-auto mb-4 text-yellow-500/50" />
                    <p className="text-white/50 mb-2 text-lg">Parent Not Registered</p>
                    <p className="text-white/40 text-sm mb-4">
                      The parent needs to complete registration before they can add payment methods.
                    </p>
                    <Button onClick={resendInvite} className="bg-[#FF007F] hover:bg-[#D6006A]">
                      <Send className="w-4 h-4 mr-2" />
                      Send Registration Invite
                    </Button>
                  </div>
                ) : paymentMethods.length === 0 ? (
                  <div className="text-center py-12">
                    <CreditCard className="w-16 h-16 mx-auto mb-4 text-white/20" />
                    <p className="text-white/50 mb-2 text-lg">No saved payment methods</p>
                    <p className="text-white/40 text-sm mb-4">
                      Send a link to the parent to add their payment card.
                    </p>
                    <Button
                      onClick={handleSendCardLink}
                      disabled={sendingCardLink}
                      className="bg-[#FF007F] hover:bg-[#D6006A]"
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      Send Card Setup Link
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Delete button at top for default card */}
                    {paymentMethods.some(card => card.is_default) && (
                      <div className="border-b border-[#333] pb-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const defaultCard = paymentMethods.find(c => c.is_default);
                            if (defaultCard) handleDeleteCard(defaultCard.id);
                          }}
                          disabled={deletingCard !== null}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    )}
                    
                    {/* Cards grid */}
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                      {paymentMethods.map((card) => (
                        <div 
                          key={card.id}
                          className={`relative rounded-2xl p-6 min-h-[200px] ${
                            card.is_default 
                              ? 'bg-gradient-to-br from-cyan-50 to-blue-50 border-2 border-cyan-200' 
                              : 'bg-gradient-to-br from-gray-100 to-gray-200 border border-gray-300'
                          }`}
                          style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
                        >
                          {/* Card Header with Brand and Default Badge */}
                          <div className="flex items-start justify-between mb-8">
                            {/* Card Brand Logo */}
                            <div className="w-14 h-10 flex items-center justify-center">
                              {card.brand.toLowerCase() === 'mastercard' ? (
                                <div className="flex">
                                  <div className="w-8 h-8 rounded-full bg-red-500 opacity-90"></div>
                                  <div className="w-8 h-8 rounded-full bg-orange-400 opacity-90 -ml-4"></div>
                                </div>
                              ) : card.brand.toLowerCase() === 'visa' ? (
                                <span className="text-2xl font-bold italic text-blue-800">VISA</span>
                              ) : card.brand.toLowerCase() === 'amex' ? (
                                <span className="text-lg font-bold text-blue-600">AMEX</span>
                              ) : (
                                <span className="text-sm font-bold text-gray-700">{card.brand}</span>
                              )}
                            </div>
                            
                            {/* Default Badge */}
                            {card.is_default && (
                              <Badge className="bg-emerald-500 text-white border-0 px-3 py-1">
                                Default
                              </Badge>
                            )}
                          </div>
                          
                          {/* Card Number */}
                          <div className="mb-6">
                            <p className="text-gray-900 font-mono text-xl tracking-wider">
                              <span className="mr-3">****</span>
                              <span className="mr-3">****</span>
                              <span className="mr-3">****</span>
                              <span className="text-2xl font-bold">{card.last4}</span>
                            </p>
                          </div>
                          
                          {/* Expiry Date */}
                          <div className="flex items-end justify-between">
                            <div>
                              <p className="text-gray-500 text-xs uppercase tracking-wider">Expiry</p>
                              <p className="text-gray-500 text-xs uppercase tracking-wider">Date</p>
                              <p className="text-gray-900 font-bold text-xl">
                                {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                              </p>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex gap-2">
                              {!card.is_default && (
                                <Button
                                  size="sm"
                                  onClick={() => handleSetDefault(card.id)}
                                  disabled={settingDefault === card.id}
                                  className="bg-blue-500 hover:bg-blue-600 text-white text-xs"
                                >
                                  {settingDefault === card.id ? '...' : 'Set Default'}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteCard(card.id)}
                                disabled={deletingCard === card.id}
                                className="text-red-500 hover:bg-red-100 hover:text-red-600"
                              >
                                {deletingCard === card.id ? (
                                  <div className="animate-spin w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attendance Tab */}
          <TabsContent value="attendance">
            <Card className="bg-[#111] border-[#333]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <History className="w-5 h-5 text-[#FF007F]" />
                  Attendance History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!student.attendance_history || student.attendance_history.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">No attendance records yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {student.attendance_history.map((record, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-3 rounded-lg bg-[#1a1a1a] border border-[#333]"
                      >
                        <div>
                          <p className="text-white font-medium">{formatDate(record.date)}</p>
                          <p className="text-white/50 text-sm">{record.class_name || 'Class'}</p>
                        </div>
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Present
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Student Details Dialog */}
        <Dialog open={editingStudentDetails} onOpenChange={setEditingStudentDetails}>
          <DialogContent className="bg-[#111] border-[#333]">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Student Details</DialogTitle>
              <p className="text-white/60 text-sm">Update the student's information.</p>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-white/70 mb-1 block">Full Name *</label>
                <Input
                  value={studentDetailsForm.name}
                  onChange={(e) => setStudentDetailsForm(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-[#1a1a1a] border-[#333] text-white"
                  placeholder="Student's full name"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-white/70 mb-1 block">Date of Birth</label>
                <Input
                  type="date"
                  value={studentDetailsForm.birth_date?.split('T')[0] || ''}
                  onChange={(e) => setStudentDetailsForm(prev => ({ ...prev, birth_date: e.target.value }))}
                  className="bg-[#1a1a1a] border-[#333] text-white"
                />
              </div>
              <div>
                <label className="text-sm text-white/70 mb-1 block">Gender</label>
                <select
                  value={studentDetailsForm.gender || ''}
                  onChange={(e) => setStudentDetailsForm(prev => ({ ...prev, gender: e.target.value }))}
                  className="w-full bg-[#1a1a1a] border border-[#333] text-white rounded-md px-3 py-2"
                >
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-white/70 mb-1 block">Medical Conditions / Notes</label>
                <textarea
                  value={studentDetailsForm.medical_conditions || ''}
                  onChange={(e) => setStudentDetailsForm(prev => ({ ...prev, medical_conditions: e.target.value }))}
                  className="w-full bg-[#1a1a1a] border border-[#333] text-white rounded-md px-3 py-2 min-h-[80px]"
                  placeholder="Any medical conditions or notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingStudentDetails(false)} className="border-[#333] text-white">
                Cancel
              </Button>
              <Button onClick={saveStudentDetails} disabled={saving} className="bg-[#FF007F] hover:bg-[#D6006A]">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Primary Guardian Dialog */}
        <Dialog open={editingPrimaryGuardian} onOpenChange={setEditingPrimaryGuardian}>
          <DialogContent className="bg-[#111] border-[#333]">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Primary Guardian</DialogTitle>
              <p className="text-white/60 text-sm">Update the primary guardian's contact information. If you change the email, you may need to resend the registration invite.</p>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white/70 mb-1 block">First Name</label>
                  <Input
                    value={primaryGuardianForm.parent_first_name}
                    onChange={(e) => setPrimaryGuardianForm(prev => ({ ...prev, parent_first_name: e.target.value }))}
                    className="bg-[#1a1a1a] border-[#333] text-white"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Last Name</label>
                  <Input
                    value={primaryGuardianForm.parent_last_name}
                    onChange={(e) => setPrimaryGuardianForm(prev => ({ ...prev, parent_last_name: e.target.value }))}
                    className="bg-[#1a1a1a] border-[#333] text-white"
                    placeholder="Last name"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-white/70 mb-1 block">Email *</label>
                <Input
                  type="email"
                  value={primaryGuardianForm.parent_email}
                  onChange={(e) => setPrimaryGuardianForm(prev => ({ ...prev, parent_email: e.target.value }))}
                  className="bg-[#1a1a1a] border-[#333] text-white"
                  placeholder="email@example.com"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-white/70 mb-1 block">Phone</label>
                <Input
                  type="tel"
                  value={primaryGuardianForm.parent_phone}
                  onChange={(e) => setPrimaryGuardianForm(prev => ({ ...prev, parent_phone: e.target.value }))}
                  className="bg-[#1a1a1a] border-[#333] text-white"
                  placeholder="Phone number"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingPrimaryGuardian(false)} className="border-[#333] text-white">
                Cancel
              </Button>
              <Button onClick={savePrimaryGuardian} disabled={saving} className="bg-[#FF007F] hover:bg-[#D6006A]">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Second Guardian Dialog */}
        <Dialog open={editingGuardian2} onOpenChange={setEditingGuardian2}>
          <DialogContent className="bg-[#111] border-[#333]">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Second Guardian</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white/70 mb-1 block">First Name</label>
                  <Input
                    value={guardian2Form.guardian2_first_name}
                    onChange={(e) => setGuardian2Form(prev => ({ ...prev, guardian2_first_name: e.target.value }))}
                    className="bg-[#1a1a1a] border-[#333] text-white"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="text-sm text-white/70 mb-1 block">Last Name</label>
                  <Input
                    value={guardian2Form.guardian2_last_name}
                    onChange={(e) => setGuardian2Form(prev => ({ ...prev, guardian2_last_name: e.target.value }))}
                    className="bg-[#1a1a1a] border-[#333] text-white"
                    placeholder="Last name"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-white/70 mb-1 block">Email</label>
                <Input
                  type="email"
                  value={guardian2Form.guardian2_email}
                  onChange={(e) => setGuardian2Form(prev => ({ ...prev, guardian2_email: e.target.value }))}
                  className="bg-[#1a1a1a] border-[#333] text-white"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label className="text-sm text-white/70 mb-1 block">Phone</label>
                <Input
                  type="tel"
                  value={guardian2Form.guardian2_phone}
                  onChange={(e) => setGuardian2Form(prev => ({ ...prev, guardian2_phone: e.target.value }))}
                  className="bg-[#1a1a1a] border-[#333] text-white"
                  placeholder="Phone number"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="receives_emails"
                  checked={guardian2Form.guardian2_receives_emails}
                  onCheckedChange={(checked) => setGuardian2Form(prev => ({ ...prev, guardian2_receives_emails: checked }))}
                  className="border-white/30 data-[state=checked]:bg-[#FF007F]"
                />
                <label htmlFor="receives_emails" className="text-white/70 text-sm cursor-pointer">
                  Second guardian receives all school emails
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingGuardian2(false)} className="border-[#333] text-white">
                Cancel
              </Button>
              <Button onClick={saveGuardian2} disabled={saving} className="bg-[#FF007F] hover:bg-[#D6006A]">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Mailing Lists Dialog */}
        <Dialog open={editingLists} onOpenChange={setEditingLists}>
          <DialogContent className="bg-[#111] border-[#333]">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Mailing Lists</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-white/70 text-sm mb-4">Select which mailing lists this student belongs to:</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {mailingLists.map(list => (
                  <div 
                    key={list.name}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedLists.includes(list.name)
                        ? 'bg-purple-500/20 border-purple-500'
                        : 'bg-[#1a1a1a] border-[#333] hover:border-purple-500/50'
                    }`}
                    onClick={() => toggleList(list.name)}
                  >
                    <Checkbox
                      checked={selectedLists.includes(list.name)}
                      onCheckedChange={() => toggleList(list.name)}
                      className="border-white/30 data-[state=checked]:bg-purple-500"
                    />
                    <div className="flex-1">
                      <p className="text-white font-medium">{list.name}</p>
                      {list.description && (
                        <p className="text-white/50 text-xs">{list.description}</p>
                      )}
                    </div>
                    <span className="text-white/30 text-xs">{list.student_count} students</span>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingLists(false)} className="border-[#333] text-white">
                Cancel
              </Button>
              <Button onClick={saveMailingLists} disabled={saving} className="bg-[#FF007F] hover:bg-[#D6006A]">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Class Dialog */}
        <Dialog open={addClassModal} onOpenChange={setAddClassModal}>
          <DialogContent className="bg-[#111] border-[#333] sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#FF007F]" />
                Add Class to {student?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 overflow-y-auto flex-1">
              {loadingClasses ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" />
                </div>
              ) : availableClasses.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">No additional classes available</p>
                  <p className="text-white/30 text-sm mt-2">This student is already enrolled in all available classes.</p>
                </div>
              ) : (
                <>
                  <p className="text-white/70 text-sm mb-4">Select one or more classes to add:</p>
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                    {availableClasses.map(cls => {
                      const isSelected = selectedClassIds.includes(cls.id);
                      const pricingOption = selectedPricingOptions[cls.id] || 'six_weeks';
                      
                      // Calculate display price based on selected option
                      let displayPrice;
                      switch (pricingOption) {
                        case 'six_weeks':
                          displayPrice = cls.six_week_price || cls.term_price || 0;
                          break;
                        case 'term':
                          displayPrice = cls.term_price || 0;
                          break;
                        case 'monthly':
                          displayPrice = cls.subscription_price || 0;
                          break;
                        case 'one_off':
                          displayPrice = cls.one_off_price || 0;
                          break;
                        case 'drop_in':
                          displayPrice = cls.drop_in_price || 0;
                          break;
                        default: // 'six_weeks' as default
                          displayPrice = cls.six_week_price || cls.term_price || 0;
                      }
                      const fullPrice = displayPrice;
                      
                      // Apply pro-rating only to 6-week bookings
                      if (isProRated && weeksRemaining < 6 && pricingOption === 'six_weeks') {
                        displayPrice = (displayPrice / 6) * weeksRemaining;
                      }
                      
                      return (
                        <div
                          key={cls.id}
                          className={`p-3 rounded-lg border transition-all ${
                            isSelected
                              ? 'bg-[#FF007F]/20 border-[#FF007F]'
                              : 'bg-[#1a1a1a] border-[#333] hover:border-[#FF007F]/50'
                          }`}
                        >
                          {/* Main row - clickable to toggle selection */}
                          <div 
                            className="flex items-center gap-3 cursor-pointer"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleClassSelection(cls.id, cls);
                            }}
                            data-testid={`class-option-${cls.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleClassSelection(cls.id, cls)}
                              className="w-4 h-4 accent-[#FF007F] cursor-pointer"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="flex-1">
                              <p className="text-white font-medium">{cls.name}</p>
                              {cls.description && (
                                <p className="text-white/50 text-xs line-clamp-1">{cls.description}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="text-[#FF007F] font-semibold">£{displayPrice.toFixed(2)}</span>
                              {isProRated && weeksRemaining < 6 && pricingOption === 'six_weeks' && (
                                <p className="text-white/40 text-xs line-through">£{fullPrice}</p>
                              )}
                            </div>
                          </div>
                          
                          {/* Pricing options - shown when selected */}
                          {isSelected && (
                            <div className="mt-3 ml-7 space-y-2" onClick={(e) => e.stopPropagation()}>
                              {/* 6 Weeks (Half Term) option */}
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`pricing-${cls.id}`}
                                  checked={pricingOption === 'six_weeks'}
                                  onChange={() => setPricingOption(cls.id, 'six_weeks')}
                                  className="accent-[#FF007F]"
                                />
                                <span className="text-white text-sm">
                                  6 Weeks — Half Term <span className="text-white/50">(£{cls.six_week_price || (cls.term_price ? cls.term_price / 2 : 0) || 0})</span>
                                </span>
                              </label>

                              {/* 12-week Full Term option retired — UPA bills every 6 weeks. */}
                              
                              {/* Monthly subscription option */}
                              {cls.subscription_price > 0 && (
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`pricing-${cls.id}`}
                                    checked={pricingOption === 'monthly'}
                                    onChange={() => setPricingOption(cls.id, 'monthly')}
                                    className="accent-[#FF007F]"
                                  />
                                  <span className="text-white text-sm">
                                    Monthly <span className="text-white/50">(£{cls.subscription_price}/month)</span>
                                  </span>
                                </label>
                              )}
                              
                              {/* One-off session option */}
                              {cls.one_off_price > 0 && (
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`pricing-${cls.id}`}
                                    checked={pricingOption === 'one_off'}
                                    onChange={() => setPricingOption(cls.id, 'one_off')}
                                    className="accent-[#FF007F]"
                                  />
                                  <span className="text-white text-sm">
                                    One-Off <span className="text-white/50">(£{cls.one_off_price})</span>
                                  </span>
                                </label>
                              )}
                              
                              {/* Drop-in option */}
                              {cls.drop_in_price > 0 && (
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`pricing-${cls.id}`}
                                    checked={pricingOption === 'drop_in'}
                                    onChange={() => setPricingOption(cls.id, 'drop_in')}
                                    className="accent-[#FF007F]"
                                  />
                                  <span className="text-white text-sm">
                                    Drop-In <span className="text-white/50">(£{cls.drop_in_price})</span>
                                  </span>
                                </label>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Mid-term joining option */}
                  <div className="mt-4 p-3 border border-[#333] bg-[#1a1a1a] rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        id="prorated"
                        checked={isProRated}
                        onChange={(e) => setIsProRated(e.target.checked)}
                        className="w-4 h-4 accent-[#FF007F] cursor-pointer"
                      />
                      <label htmlFor="prorated" className="cursor-pointer font-medium text-white text-sm">
                        Joining mid-term? (Pro-rated pricing)
                      </label>
                    </div>
                    
                    {activeTerm && (
                      <p className="text-xs text-white/50 ml-6">
                        Current term: {activeTerm.name} (ends {new Date(activeTerm.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})
                      </p>
                    )}
                    
                    {isProRated && (
                      <div className="ml-6 mt-3 flex items-center gap-3">
                        <label className="text-sm text-white/70 whitespace-nowrap">Weeks remaining:</label>
                        <Input 
                          type="number"
                          min="1"
                          max="12"
                          value={weeksRemaining}
                          onChange={(e) => setWeeksRemaining(parseInt(e.target.value) || 1)}
                          className="w-20 bg-[#111] border-[#333] text-white"
                        />
                        <span className="text-white/50 text-sm">of 6 week half-term</span>
                      </div>
                    )}
                  </div>

                  {/* Mark as already paid (record-only, no Stripe charge) */}
                  <div className="mt-3 p-3 border border-amber-500/30 bg-amber-500/5 rounded-lg">
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="mark-as-paid"
                        data-testid="mark-as-paid-checkbox"
                        checked={markAsPaid}
                        onChange={(e) => setMarkAsPaid(e.target.checked)}
                        className="w-4 h-4 accent-amber-500 cursor-pointer mt-0.5"
                      />
                      <label htmlFor="mark-as-paid" className="cursor-pointer text-sm text-amber-200">
                        <span className="font-medium">Mark as already paid</span> — record this booking with <span className="font-semibold">payment_status = paid</span> without charging the card. Use for historical payments collected outside the app (or bookings lost in a data issue).
                      </label>
                    </div>
                  </div>
                  
                  {selectedClassIds.length > 0 && (() => {
                    const items = selectedClassIds.map(classId => {
                      const cls = availableClasses.find(c => c.id === classId);
                      const pricingOption = selectedPricingOptions[classId] || 'six_weeks';
                      return { classId, name: cls?.name || '', price: getPriceForOption(cls, pricingOption), pricingOption };
                    });
                    const isProRateActive = isProRated && weeksRemaining < 6 && items.some(it => it.pricingOption === 'six_weeks');
                    const bundleAdjusted = (!isProRateActive && !markAsPaid) ? computeBundleAdjustedTotal(items) : null;
                    const topUpLine = bundleAdjusted?.lineDetails?.find(l => l.topUpReason);
                    return (
                    <div className="mt-4 p-3 bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-white/70">{selectedClassIds.length} class(es) selected</span>
                          {isProRated && weeksRemaining < 6 && (
                            <p className="text-amber-400 text-xs mt-1">
                              Pro-rated for {weeksRemaining} weeks
                            </p>
                          )}
                          {topUpLine && (
                            <p className="text-emerald-400 text-xs mt-1" data-testid="bundle-topup-note">
                              ★ Bundle discount: {topUpLine.topUpReason}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="text-white font-bold text-lg">
                            £{getSelectedClassesTotal().toFixed(2)}
                          </span>
                          {isProRated && weeksRemaining < 6 && (
                            <p className="text-white/40 text-xs line-through">
                              £{getFullTermPrice().toFixed(2)}
                            </p>
                          )}
                          {topUpLine && (
                            <p className="text-white/40 text-xs line-through">
                              £{getFullTermPrice().toFixed(2)}
                            </p>
                          )}
                        </div>
                      </div>
                      {paymentMethods.length > 0 && !markAsPaid && (
                        <p className="text-green-400 text-xs mt-2 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Payment will be charged to saved card
                        </p>
                      )}
                      {paymentMethods.length === 0 && !markAsPaid && (
                        <p className="text-yellow-400 text-xs mt-2 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          No saved card - payment will be marked as pending
                        </p>
                      )}
                      {markAsPaid && (
                        <p className="text-amber-300 text-xs mt-2 flex items-center gap-1" data-testid="mark-paid-preview-hint">
                          <CheckCircle className="w-3 h-3" />
                          Will be recorded as <strong>already paid</strong> — no Stripe charge.
                        </p>
                      )}
                    </div>
                  );})()}
                </>
              )}
            </div>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setAddClassModal(false)} 
                className="border-[#333] text-white"
                disabled={addingClasses}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddClasses} 
                disabled={addingClasses || selectedClassIds.length === 0}
                className="bg-[#FF007F] hover:bg-[#D6006A]"
                data-testid="confirm-add-class-btn"
              >
                {addingClasses ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add {selectedClassIds.length > 0 ? `${selectedClassIds.length} Class(es)` : 'Classes'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Pre-Selected Classes Dialog */}
        <Dialog open={editingPreSelectedClasses} onOpenChange={setEditingPreSelectedClasses}>
          <DialogContent className="bg-[#111] border-[#333] sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-purple-400" />
                Edit Pre-Selected Classes
              </DialogTitle>
            </DialogHeader>
            <p className="text-white/60 text-sm">
              Select which classes {student?.name} should be enrolled in when the parent registers. 
              This will affect the pricing shown during registration.
            </p>
            <div className="py-4 overflow-y-auto flex-1">
              {loadingClasses ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
                </div>
              ) : availableClasses.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">No classes available</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
                  {availableClasses.map(cls => {
                    const isSelected = preSelectedClassIds.includes(cls.id);
                    return (
                      <div
                        key={cls.id}
                        onClick={() => togglePreSelectedClass(cls.id)}
                        className={`p-3 rounded-lg cursor-pointer transition-all border ${
                          isSelected
                            ? 'bg-purple-500/20 border-purple-500/50'
                            : 'bg-white/5 border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={isSelected}
                              className="border-white/30 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
                            />
                            <div>
                              <p className="text-white font-medium">{cls.name}</p>
                              <p className="text-white/50 text-sm">
                                {cls.day} {cls.time} • £{(cls.term_price || 0).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEditingPreSelectedClasses(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={savePreSelectedClasses}
                disabled={savingPreSelectedClasses}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {savingPreSelectedClasses ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Bootcamp Dialog */}
        <Dialog open={addBootcampOpen} onOpenChange={setAddBootcampOpen}>
          <DialogContent className="bg-[#111] border-purple-500/30 text-white max-w-md" data-testid="add-bootcamp-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-purple-400" />
                Add Bootcamp to {student?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-white/70 uppercase tracking-wider">Pick a bootcamp</label>
                {bootcampLoading ? (
                  <div className="flex items-center gap-2 text-white/50 text-sm py-3">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading bootcamps…
                  </div>
                ) : (
                  <div className="space-y-2 mt-2 max-h-64 overflow-y-auto">
                    {bootcamps.length === 0 && (
                      <p className="text-white/50 text-sm">No active bootcamps</p>
                    )}
                    {bootcamps.map((bc) => (
                      <label
                        key={bc.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                          bootcampForm.bootcamp_id === bc.id
                            ? 'border-purple-500 bg-purple-500/10'
                            : 'border-[#333] hover:border-[#555]'
                        }`}
                        data-testid={`bootcamp-option-${bc.id}`}
                      >
                        <input
                          type="radio"
                          name="bootcamp"
                          value={bc.id}
                          checked={bootcampForm.bootcamp_id === bc.id}
                          onChange={(e) => setBootcampForm({ ...bootcampForm, bootcamp_id: e.target.value })}
                          className="mt-1 accent-purple-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-medium text-sm truncate">{bc.name}</div>
                          <div className="text-white/50 text-xs">
                            {bc.start_date} → {bc.end_date} · ages {bc.age_min}–{bc.age_max}
                          </div>
                          <div className="text-purple-300 text-sm font-semibold">£{Number(bc.price).toFixed(2)}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Live price preview */}
              {bootcampForm.bootcamp_id && (
                <div
                  className="rounded-lg border border-purple-500/30 bg-purple-500/5 p-3 text-sm"
                  data-testid="bootcamp-price-preview"
                >
                  {previewLoading || !bootcampPreview ? (
                    <div className="flex items-center gap-2 text-white/60">
                      <Loader2 className="w-3 h-3 animate-spin" /> Calculating price…
                    </div>
                  ) : bootcampPreview.already_booked ? (
                    <div className="text-amber-400">
                      ⚠ {bootcampPreview.student_name} is already booked on this camp
                      {bootcampPreview.already_booked_status
                        ? ` (status: ${bootcampPreview.already_booked_status})`
                        : ''}.
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {bootcampPreview.sibling_discount_applied ? (
                        <>
                          <div className="text-pink-300 text-xs">
                            ✓ 30% sibling discount — applies because{' '}
                            <strong>{bootcampPreview.paid_sibling_name}</strong> has already paid in full.
                          </div>
                          <div className="flex justify-between items-center pt-1">
                            <span className="text-white/70">Will charge:</span>
                            <span className="text-lg font-bold text-purple-300" data-testid="preview-final-amount">
                              £{Number(bootcampPreview.final_amount).toFixed(2)}{' '}
                              <span className="text-xs text-white/40 line-through ml-1 font-normal">
                                £{Number(bootcampPreview.base_price).toFixed(2)}
                              </span>
                            </span>
                          </div>
                          <div className="text-emerald-400 text-xs text-right">
                            Saves £{Number(bootcampPreview.sibling_discount_amount).toFixed(2)}
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-between items-center">
                          <span className="text-white/70">Will charge:</span>
                          <span className="text-lg font-bold text-white" data-testid="preview-final-amount">
                            £{Number(bootcampPreview.final_amount).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="border-t border-[#222] pt-3">
                <label className="flex items-center gap-2 text-sm text-amber-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bootcampForm.returning_student}
                    onChange={(e) => setBootcampForm({
                      ...bootcampForm,
                      returning_student: e.target.checked,
                      // Mutually exclusive with mark_as_paid mode
                      mark_as_paid: e.target.checked ? false : bootcampForm.mark_as_paid,
                    })}
                    className="accent-amber-500"
                    data-testid="bootcamp-returning-student"
                  />
                  Returning student — paid deposit, owes a remaining balance
                </label>
                {bootcampForm.returning_student && (
                  <div className="space-y-2 mt-2 ml-6 p-3 bg-amber-500/5 border border-amber-500/20 rounded">
                    <div>
                      <label className="text-xs text-white/80">Balance still owed (£) <span className="text-red-400">*</span></label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={bootcampForm.amount_owed}
                        onChange={(e) => setBootcampForm({ ...bootcampForm, amount_owed: e.target.value })}
                        className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                        placeholder="e.g. 250.00"
                        data-testid="returning-amount-owed"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/70">Deposit already paid (£) — optional, for the audit note</label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={bootcampForm.deposit_paid}
                        onChange={(e) => setBootcampForm({ ...bootcampForm, deposit_paid: e.target.value })}
                        className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                        placeholder="e.g. 25.00"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/70">Admin note (kept on the booking)</label>
                      <Input
                        value={bootcampForm.admin_note}
                        onChange={(e) => setBootcampForm({ ...bootcampForm, admin_note: e.target.value })}
                        className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                        placeholder="e.g. £25 deposit paid 12 Dec 2025 cash"
                        data-testid="returning-admin-note"
                      />
                    </div>
                    <label className="flex items-center gap-2 text-xs text-white/80 cursor-pointer pt-1">
                      <input
                        type="checkbox"
                        checked={bootcampForm.send_email}
                        onChange={(e) => setBootcampForm({ ...bootcampForm, send_email: e.target.checked })}
                        className="accent-amber-500"
                      />
                      Email parent a Pay Now / Pay in Instalments link for the balance
                    </label>
                    <p className="text-xs text-amber-300/80">
                      Spot is reserved immediately. Parent receives one email with a button to choose Pay Now or 3 Instalments — both use their saved card.
                    </p>
                  </div>
                )}
              </div>

              <div className={bootcampForm.returning_student ? 'opacity-50 pointer-events-none border-t border-[#222] pt-3' : 'border-t border-[#222] pt-3'}>
                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bootcampForm.mark_as_paid}
                    disabled={bootcampForm.returning_student}
                    onChange={(e) => setBootcampForm({ ...bootcampForm, mark_as_paid: e.target.checked })}
                    className="accent-emerald-500"
                    data-testid="bootcamp-mark-paid"
                  />
                  Mark as already paid (no Stripe charge)
                </label>
                {bootcampForm.mark_as_paid && !bootcampForm.returning_student && (
                  <div className="space-y-2 mt-2 ml-6">
                    <div>
                      <label className="text-xs text-white/70">Amount paid (£) — optional</label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={bootcampForm.paid_amount}
                        onChange={(e) => setBootcampForm({ ...bootcampForm, paid_amount: e.target.value })}
                        className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                        placeholder="Leave blank to use bootcamp price (with sibling discount if applicable)"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-white/70">Note</label>
                      <Input
                        value={bootcampForm.paid_note}
                        onChange={(e) => setBootcampForm({ ...bootcampForm, paid_note: e.target.value })}
                        className="bg-[#0a0a0a] border-[#333] text-white mt-1"
                        placeholder="e.g. paid via bank transfer"
                      />
                    </div>
                  </div>
                )}
                {!bootcampForm.mark_as_paid && !bootcampForm.returning_student && (
                  <p className="text-xs text-white/50 mt-2">
                    Booking will be created as <strong>pending</strong>. You can then send the parent a payment link
                    (Pay in Full or 3 Instalments) from the Bootcamps admin page.
                  </p>
                )}
                <p className="text-xs text-pink-400 mt-2">
                  💡 If this parent already has a paid camp booking on the same camp, a 30% sibling discount applies automatically.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setAddBootcampOpen(false)}
                className="border-[#333] text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={submitAddBootcamp}
                disabled={savingBootcamp || !bootcampForm.bootcamp_id}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="bootcamp-submit"
              >
                {savingBootcamp ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding…</> : 'Add bootcamp'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Shareable Link Modal — used for password reset + create-login flows */}
        <Dialog open={!!linkModal} onOpenChange={(open) => { if (!open) setLinkModal(null); }}>
          <DialogContent className="bg-[#111] border-pink-500/30 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-pink-400" />
                {linkModal?.title}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-white/80 text-sm">{linkModal?.message}</p>
              <textarea
                readOnly
                value={linkModal?.url || ''}
                onClick={(e) => e.target.select()}
                rows={3}
                className="w-full bg-[#0a0a0a] border border-[#333] text-white rounded px-3 py-2 font-mono text-xs break-all"
                data-testid="shareable-link-textarea"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={async () => {
                    const ok = await copyToClipboard(linkModal?.url || '');
                    if (ok) toast.success('Link copied to clipboard'); else toast.error('Copy failed — long-press the box and select all');
                  }}
                  className="bg-pink-600 hover:bg-pink-700"
                  data-testid="copy-link-btn"
                >
                  <Copy className="w-4 h-4 mr-1" /> Copy link
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const subject = encodeURIComponent('Your UPA account');
                    const body = encodeURIComponent(
                      `Hi,\n\nHere's your link to set your password / log in to your UPA account:\n\n${linkModal?.url}\n\nThanks,\nUPA Team`,
                    );
                    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                  }}
                  className="border-[#333] text-white"
                >
                  <Mail className="w-4 h-4 mr-1" /> Send via email app
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const text = encodeURIComponent(`Hi, here's your UPA account link: ${linkModal?.url}`);
                    window.open(`sms:?body=${text}`, '_blank');
                  }}
                  className="border-[#333] text-white"
                >
                  Send via SMS
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const text = encodeURIComponent(`Hi, here's your UPA account link: ${linkModal?.url}`);
                    window.open(`https://wa.me/?text=${text}`, '_blank');
                  }}
                  className="border-[#333] text-white"
                >
                  Send via WhatsApp
                </Button>
              </div>
              <p className="text-xs text-white/40">
                💡 If the parent's email blocks our messages, the SMS/WhatsApp buttons open your default app pre-filled — just pick their contact and hit send.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLinkModal(null)} className="border-[#333] text-white">
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Scholarship / discount editor */}
        <Dialog open={!!discountModal} onOpenChange={(open) => { if (!open) setDiscountModal(null); }}>
          <DialogContent className="bg-[#111] border-yellow-500/30 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-yellow-300">Scholarship / Discount</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-white/70">
                Applies a percentage discount automatically to every new booking and renewal for this student. Set to <span className="font-semibold">100</span> for a full scholarship, <span className="font-semibold">50</span> for half-price, <span className="font-semibold">0</span> to remove.
              </p>
              <label className="block">
                <span className="text-white/70">Discount %</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={discountModal?.percent ?? 0}
                  onChange={(e) => setDiscountModal({ ...discountModal, percent: e.target.value })}
                  className="w-full mt-1 bg-[#0a0a0a] border border-[#333] rounded px-3 py-2 text-white"
                  data-testid="discount-percent-input"
                />
              </label>
              <label className="block">
                <span className="text-white/70">Reason / note (visible to admin only)</span>
                <textarea
                  rows={2}
                  value={discountModal?.note || ''}
                  onChange={(e) => setDiscountModal({ ...discountModal, note: e.target.value })}
                  placeholder="e.g. Scholarship 2026, Staff child, Hardship review approved Feb 2026"
                  className="w-full mt-1 bg-[#0a0a0a] border border-[#333] rounded px-3 py-2 text-white"
                  data-testid="discount-note-input"
                />
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDiscountModal(null)} className="border-[#333] text-white" disabled={savingDiscount}>
                Cancel
              </Button>
              <Button onClick={saveDiscount} disabled={savingDiscount} className="bg-yellow-500 hover:bg-yellow-600 text-black" data-testid="save-discount-btn">
                {savingDiscount ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default StudentDetail;
